import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/strategyxs.png'


const StrategyPage = () => {
    window.scroll(0, 0)

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const strategyStats = [
        { number: '92%', label: 'of businesses with a clear digital strategy achieve their growth goals and objectives' },
        { number: '4.5x', label: 'higher ROI for companies with documented strategic marketing plans and frameworks' },
        { number: '75%', label: 'increase in campaign effectiveness with strategic alignment and planning' },
        { number: '60%', label: 'faster time-to-market with strategic planning frameworks and execution' }
    ];

    const strategyApproaches = [
        {
            icon: 'fas fa-chess',
            title: 'Digital Strategy Development & Planning',
            desc: 'Comprehensive digital roadmap creation aligning business objectives with digital capabilities, market opportunities, and competitive positioning for sustainable growth.'
        },
        {
            icon: 'fas fa-bullseye',
            title: 'Goal Alignment & KPI Definition',
            desc: 'Clear definition of business objectives, success metrics, and key performance indicators to measure progress and optimize marketing performance.'
        },
        {
            icon: 'fas fa-sitemap',
            title: 'Multi-Channel Strategy & Integration',
            desc: 'Strategic planning for integrated digital presence across SEO, PPC, social media, content, and email marketing for cohesive brand experience.'
        },
        {
            icon: 'fas fa-chart-network',
            title: 'Competitive Strategy & Market Analysis',
            desc: 'In-depth analysis of competitor strategies, market positioning, and identification of strategic advantages and opportunities for differentiation.'
        },
        {
            icon: 'fas fa-road',
            title: 'Strategic Roadmap & Implementation',
            desc: 'Detailed implementation timelines, resource allocation plans, and phased approach to achieving digital transformation and growth goals.'
        },
        {
            icon: 'fas fa-sync-alt',
            title: 'Performance Optimization & Strategy Iteration',
            desc: 'Continuous strategy refinement based on performance data, market changes, and emerging opportunities for sustained competitive advantage.'
        }
    ];

    const benefits = [
        ['Strategic Clarity & Direction', 'Clear strategic roadmap aligning all digital marketing efforts with core business objectives'],
        ['Maximized Marketing ROI', 'Optimized resource allocation and budget planning for maximum return on investment'],
        ['Sustainable Competitive Advantage', 'Strategic positioning that differentiates your brand in competitive digital markets'],
        ['Scalable Growth Framework', 'Structured approach to scaling digital operations and market presence efficiently'],
        ['Proactive Risk Mitigation', 'Strategic identification and management of potential challenges and market obstacles']
    ];

    const targetBusinesses = [
        'Businesses preparing for digital transformation or major market expansion',
        'Companies launching new products or entering new market segments',
        'Organizations seeking to optimize and align their existing digital marketing efforts',
        'Startups building foundational digital strategy for scalable growth and market entry',
        'Enterprises undergoing digital restructuring or strategic realignment',
        'Brands facing increased competition and needing strategic differentiation and positioning'
    ];

    const strategyQuestions = [
        'What are your core business objectives and how can digital marketing support them effectively?',
        'How should you allocate resources across different digital channels for maximum impact?',
        'What competitive advantages can you build and sustain through digital marketing?',
        'How will you measure success and optimize marketing performance over time?',
        'What emerging market opportunities should inform your strategic direction?'
    ];

    return (
        <div className="strategy sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Digital Strategy</span> Services
                    </h1>
                    <p>Strategic planning and execution frameworks for sustainable business growth and competitive advantage</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={12}>
                                <p className="sv-lead">
                                    A well-defined <strong>digital strategy</strong> is the cornerstone of sustainable business growth in today's competitive digital landscape.
                                </p>
                                <p className="sv-description">
                                    Reachtix <strong>Digital Strategy</strong> services provide comprehensive strategic planning that aligns your business objectives with digital capabilities, market opportunities, and competitive positioning. Our <strong>strategic marketing</strong> approach ensures every digital effort drives measurable results.
                                </p>
                                <p className="sv-description">
                                    We transform complex challenges into clear, actionable roadmaps that drive measurable results and sustainable competitive advantage through integrated <strong>digital strategy</strong> frameworks and execution.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Strategy Development
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Strategy Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Digital Strategy</span> Drives Business Success
                        </h2>
                        <Row className="sv-stats-grid">
                            {strategyStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Digital Strategy Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Digital Strategy?</h2>
                                <p className="sv-section-description">
                                    <strong>Digital strategy</strong> is the comprehensive plan that defines how your organization will leverage digital technologies and channels to achieve business objectives, engage customers, and outperform competitors. It's not just about individual tactics, but about creating an integrated framework that aligns all <strong>digital marketing</strong> efforts with your overall business vision.
                                </p>
                                <p className="sv-section-description">
                                    An effective <strong>strategic marketing</strong> plan ensures every digital investment contributes to your overarching goals, resources, and market position through coordinated execution and measurement.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Strategic Questions We Address:</h4>
                                    {strategyQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Digital Strategy Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Why Digital Strategy Drives Competitive Success</h2>
                                <p className="sv-section-description">
                                    In today's rapidly evolving digital landscape, having a clear strategic direction is essential for sustainable growth and competitive advantage through effective <strong>strategic planning</strong>.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Clear Strategic Direction & Focus</h5>
                                        <p>Align all digital marketing efforts with core business objectives and priorities</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Optimized Resource Allocation</h5>
                                        <p>Strategic budgeting and resource planning for maximum marketing ROI</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-shield-alt"></i>
                                        <h5>Competitive Market Positioning</h5>
                                        <p>Differentiate your brand and capture market share strategically</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-expand-alt"></i>
                                        <h5>Scalable Growth Framework</h5>
                                        <p>Build foundations for sustainable expansion and market leadership</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Strategy Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Our Digital Strategy Framework</h2>
                                <p className="sv-section-description">
                                    We employ a comprehensive, data-driven approach to <strong>digital strategy</strong> development that ensures alignment with your business goals and market realities through proven <strong>strategic planning</strong> methodologies.
                                </p>

                                <div className="sv-approaches-grid">
                                    {strategyApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Strategic Benefits for Your Business</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="mt-4 sv-section-description">
                                    A well-crafted <strong>digital strategy</strong> transforms uncertainty into clarity, turning complex challenges into actionable opportunities for growth and differentiation in competitive markets.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Who Needs Digital Strategy Services?</h2>
                                <p className="sv-section-description">
                                    <strong>Strategic planning</strong> delivers maximum value for organizations seeking clarity, direction, and sustainable competitive advantage in their digital marketing:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're a startup building foundations or an enterprise optimizing existing operations, strategic clarity drives stronger outcomes and sustainable growth through effective <strong>digital strategy</strong>.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <h2>Ready to Build Your Strategic Advantage?</h2>
                        <p className="sv-cta-description">
                            Transform complexity into clarity with a comprehensive <strong>digital strategy</strong> that aligns your business objectives with market opportunities and digital capabilities for sustainable growth.
                        </p>
                        <p className="sv-cta-description">
                            Let Reachtix guide your strategic journey from vision to execution, ensuring every digital effort contributes to your overarching business success through proven <strong>strategic planning</strong> frameworks.
                        </p>
                        <div className="sv-cta-buttons">
                            <Button
                                tag={Link}
                                to="/contact"
                                color='primary'
                            >
                                <i className="fas fa-chess mr-2"></i>
                                Start Your Strategy Development
                            </Button>
                            <Button
                                tag={Link}
                                to="/contact"
                                color='light'
                            >
                                <i className="fas fa-comments mr-2"></i>
                                Get Strategic Consultation
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default StrategyPage;